import {
  Accessibility,
  BarChart3,
  BookOpen,
  Bug,
  CircleHelp,
  Crown,
  Database,
  Download,
  FlaskConical,
  FolderOpen,
  Gauge,
  GraduationCap,
  Home,
  Inbox,
  List,
  MousePointer2,
  Paperclip,
  Plug,
  Settings,
  Shield,
  Sliders,
  Sparkles,
  CalendarRange,
  Play,
} from "lucide-react";

export const MenuItems = [
  {
    label: "Dashboard",
    icon: Home,
    path: "/dashboard",
    matchPattern: "^/(dashboard)?$",
    title: "Welcome to Automation Control Center",
    description:
      "Track, manage, and accelerate your test automation workflows in real-time. Every test executed here brings you one step closer to faster releases and bulletproof quality.",
  },
  {
    label: "Test Cases",
    icon: FlaskConical,
    path: "/testcases/all",
    matchPattern: "^/(testcases)(/.*)?$",
    title: "Test Cases",
    description: "View and manage all created test scenarios in one place.",
  },
  {
    label: "Load Testing",
    icon: Gauge,
    path: "/loadtest/library",
    matchPattern:
      "^/(loadtest/library|testcases/performance|load-testing)(/.*)?$",
    title: "Load Test Management",
    description:
      "Organize, discover, version, and manage performance and load test suites.",
    submenu: [
      {
        label: "Load Test Explorer",
        icon: FolderOpen,
        path: "/loadtest/library",
        title: "Load Test Explorer",
        description:
          "Browse, filter, and inspect load test suites in 3-panel workspace.",
      },
      {
        label: "Load Test Builder",
        icon: Sliders,
        path: "/loadtest/library",
        title: "Load Test Builder",
        description:
          "Configure traffic profiles, requests, and scenario definitions.",
      },
      {
        label: "Executions",
        icon: Play,
        path: "/loadtest/executions",
        title: "Execution Management",
        description:
          "Monitor, control, and audit live load test executions, logs, and workers.",
      },
    ],
  },
  {
    label: "UI Elements",
    icon: MousePointer2,
    path: "/elements",
    matchPattern: "^/elements(/.*)?$",
    title: "UI Elements",
    description:
      "Create, inspect, and manage reusable selectors for automation.",
    requiredPermission: { resource: "ELEMENTS", action: "READ" },
  },
  {
    label: "Test Data",
    icon: Database,
    path: "/test-data",
    matchPattern: "^/test-data(/.*)?$",
    title: "Test Data",
    description: "Define fields and datasets profiles for your test scenarios.",
    requiredPermission: { resource: "TEST_DATA", action: "READ" },
  },
  {
    label: "Cycles",
    icon: CalendarRange,
    path: "/cycles",
    matchPattern: "^/cycles(/.*)?$",
    title: "Test Cycles",
    description:
      "Plan release cycles, assign coverage, and run manual execution.",
    submenu: [
      {
        label: "Cycles",
        icon: CalendarRange,
        path: "/cycles",
        title: "Test Cycles",
        description: "Plan release cycles and assign coverage.",
      },
      {
        label: "My Queue",
        icon: Inbox,
        path: "/cycles/queue",
        title: "My Queue",
        description: "Your assigned cases — click to execute step-by-step.",
      },
      {
        label: "Fix Queue",
        icon: Bug,
        path: "/cycles/fix-queue",
        title: "Fix Queue",
        description: "Defects assigned to you — mark fixed to trigger Retest.",
      },
      {
        label: "Lead Home",
        icon: Crown,
        path: "/cycles/lead-home",
        title: "Lead Home",
        description:
          "All active cycles across releases — your operational landing.",
      },
    ],
  },
  {
    label: "Load Testing",
    icon: Gauge,
    path: "/loadtest/library",
    matchPattern:
      "^/(loadtest/library|testcases/performance|load-testing)(/.*)?$",
    title: "Load Test Management",
    description:
      "Organize, discover, version, and manage performance and load test suites.",
  },
  {
    label: "Executions",
    icon: Play,
    path: "/loadtest/executions",
    matchPattern:
      "^/(loadtest/executions|testcases/executions|executions)(/.*)?$",
    title: "Execution Management",
    description:
      "Monitor, control, and audit live load test executions, logs, and workers.",
  },
  {
    label: "Defects",
    icon: Bug,
    path: "/defects",
    title: "Defects",
    description: "Track and manage all test defects reported from scenarios.",
  },
  {
    label: "Requirements",
    icon: List,
    path: "/test-management/requirements",
    title: "Requirements",
    description:
      "Define and manage the testing requirements for your scenarios.",
    requiredPermission: { resource: "REQUIREMENTS", action: "READ" },
  },
  {
    label: "Reports",
    path: "/reports",
    icon: BarChart3,
    title: "Reports",
    description: "Reporting and analytics.",
    requiredPermission: { resource: "REPORTS", action: "READ" },
  },
  {
    label: "Upload",
    path: "/uploads",
    icon: Paperclip,
    title: "Upload",
    description: "Upload files to reference in your test steps.",
    requiredPermission: { resource: "UPLOADS", action: "READ" },
  },
  {
    label: "Accessibility Testing",
    icon: Accessibility,
    path: "/accessibility",
    matchPattern: "^/accessibility(/.*)?$",
    title: "Accessibility Testing",
    description:
      "Scan pages for WCAG issues with axe-core. Automated scans find only part of WCAG issues — not a substitute for manual review.",
    requiredPermission: { resource: "ACCESSIBILITY", action: "READ" },
  },
];

export const FixedMenuItems = [
  {
    label: "Configuration",
    path: "/settings/projects",
    icon: Settings,
    title: "Configuration",
    description: "Configure user preferences and account details.",
    submenu: [
      {
        label: "Projects",
        icon: FolderOpen,
        path: "/settings/projects",
        title: "Projects",
        description: "Manage your projects and their settings.",
      },
      // {
      //   label: "Environments",
      //   icon: MonitorCheck,
      //   path: "/settings/environments",
      //   title: "Environments",
      //   description: "Manage test environments and their parameter key-value pairs.",
      // },
      {
        label: "Roles & Permissions",
        icon: Shield,
        path: "/settings/roles",
        title: "Roles & Permissions",
        description: "Manage what each role can do within the system.",
        requiredPermission: { resource: "ROLES", action: "READ" },
      },
      {
        label: "Integrations",
        icon: Plug,
        path: "/settings/plugins",
        title: "Integrations",
        description: "Manage and configure integrations for the application.",
        requiredPermission: { resource: "INTEGRATIONS", action: "READ" },
      },
      {
        label: "Import / Export",
        icon: Download,
        path: "/settings/import-export",
        title: "Import Export",
        description: "Manage Import and Export of data.",
        requiredPermission: { resource: "BACKUP", action: "READ" },
      },
    ],
  },
];

export const SupportMenuItems = [
  {
    label: "Guide Me",
    path: "/guideme",
    icon: GraduationCap,
    title: "Guide Me",
    description:
      "Interactive guided walkthroughs that teach the platform in context.",
  },
  {
    label: "Documentation",
    path: "/docs",
    icon: BookOpen,
    title: "Documentation",
    description: "Browse platform documentation and feature guides.",
  },
  {
    label: "Start Tour",
    path: "#tour",
    icon: Sparkles,
    title: "Start Tour",
    description: "Take a guided tour of the platform features.",
  },
  {
    label: "Help & Support",
    path: "/support",
    icon: CircleHelp,
    title: "Help & Support",
    description: "Get help and contact support.",
  },
];
